import { useMemo } from 'react'

/**
 * Animated space background for dark mode.
 * Ported from the Emergent production QSECDEF layout.
 * Stars twinkle, shooting stars cross the screen, rockets fly through.
 */
export function SpaceBackground() {
  const stars = useMemo(
    () =>
      Array.from({ length: 80 }, (_, i) => ({
        id: i,
        top: (i * 17 + 23) % 100,
        left: (i * 31 + 7) % 100,
        delay: (i * 0.3) % 4,
        duration: 2 + (i % 3),
        size: 1 + (i % 2),
        opacity: 0.3 + (i % 5) * 0.1,
      })),
    [],
  )

  const shootingStars = useMemo(
    () => [
      { delay: 0, top: 12, left: 15 },
      { delay: 5, top: 28, left: 55 },
      { delay: 10, top: 8, left: 75 },
      { delay: 15, top: 35, left: 35 },
    ],
    [],
  )

  return (
    <>
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.8; }
        }
        @keyframes shootingStar {
          0% { transform: translateX(0) translateY(0); opacity: 1; }
          70% { opacity: 1; }
          100% { transform: translateX(200px) translateY(200px); opacity: 0; }
        }
        @keyframes flySpaceship {
          0% { transform: translateX(-50px) rotate(45deg); opacity: 0; }
          10% { opacity: 0.7; }
          90% { opacity: 0.7; }
          100% { transform: translateX(calc(100vw + 50px)) rotate(45deg); opacity: 0; }
        }
        .space-star { animation: twinkle 3s ease-in-out infinite; }
        .space-shooting-star {
          width: 3px;
          height: 3px;
          background: linear-gradient(45deg, white, transparent);
          border-radius: 50%;
          animation: shootingStar 4s ease-in-out infinite;
        }
        .space-shooting-star::after {
          content: '';
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 60px;
          height: 1px;
          background: linear-gradient(to left, rgba(255,255,255,0.8), transparent);
        }
        .space-ship {
          animation: flySpaceship 20s linear infinite;
          font-size: 16px;
        }
      `}</style>
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a12] via-[#0f0f1a] to-[#0a0a15]" />
        {stars.map((star) => (
          <div
            key={star.id}
            className="absolute rounded-full bg-white space-star"
            style={{
              top: `${star.top}%`,
              left: `${star.left}%`,
              width: star.size,
              height: star.size,
              animationDelay: `${star.delay}s`,
              animationDuration: `${star.duration}s`,
              opacity: star.opacity,
            }}
          />
        ))}
        {shootingStars.map((star, i) => (
          <div
            key={`shooting-${i}`}
            className="absolute space-shooting-star"
            style={{
              top: `${star.top}%`,
              left: `${star.left}%`,
              animationDelay: `${star.delay}s`,
            }}
          />
        ))}
        <div className="absolute space-ship" style={{ top: '15%', animationDelay: '0s' }}>🚀</div>
        <div className="absolute space-ship" style={{ top: '45%', animationDelay: '8s' }}>🚀</div>
        <div className="absolute space-ship" style={{ top: '75%', animationDelay: '16s' }}>🚀</div>
      </div>
    </>
  )
}
